import { Link } from "react-router";

import { FingerprintIcon, TrophyIcon } from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { ShowViewHeader } from "@/components/refine-ui/views/show-view";
import {
  DetailField,
  DetailGrid,
  DetailSection,
  RawJsonCard,
  ShowHero,
  SystemSection,
  useShowRecord,
} from "@/components/show";
import { Badge } from "@/components/ui/badge";
import { MatchDetailsSection } from "@/pages/matches/components/details/match-details-section";
import { formatKickoff, formatScore } from "@/pages/matches/lib/match-format";
import {
  MatchViewType,
  matchViewSchema,
} from "@/pages/matches/schemas/match.view";
import { ReadOnlyNote } from "@/pages/sports/components/read-only-note";

const teamLink = (id?: string | null, name?: string | null) =>
  id ? (
    <Link to={`/teams/show/${id}`} className="hover:underline">
      {name ?? id}
    </Link>
  ) : (
    "—"
  );

export const MatchShow = () => {
  const { record, raw } = useShowRecord<MatchViewType>(
    matchViewSchema,
    (r) => `Match | ${r.homeTeam?.name ?? "?"} v ${r.awayTeam?.name ?? "?"}`,
  );

  return (
    <div className="p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <ShowViewHeader />

        {record && (
          <>
            <ShowHero
              poster={
                record.homeTeam?.media?.photos?.[0] ??
                record.awayTeam?.media?.photos?.[0]
              }
              eyebrow={record.sport}
              title={`${record.homeTeam?.name ?? "—"} v ${record.awayTeam?.name ?? "—"}`}
              subtitle={formatKickoff(record.startDate)}
              badges={
                <>
                  {record.status && (
                    <Badge variant="default">{record.status}</Badge>
                  )}
                  {record.state && (
                    <Badge variant="outline">{record.state}</Badge>
                  )}
                  {record.hide && <Badge variant="secondary">Hidden</Badge>}
                </>
              }
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <DetailSection
                title="Details"
                icon={<TrophyIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="Kickoff">
                    {formatKickoff(record.startDate)}
                  </DetailField>
                  <DetailField label="League">
                    {record.leagueId ? (
                      <Link
                        to={`/leagues/show/${record.leagueId}`}
                        className="hover:underline"
                      >
                        {record.league?.name ?? record.leagueId}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </DetailField>
                  <DetailField label="Home team">
                    {teamLink(record.homeTeamId, record.homeTeam?.name)}
                  </DetailField>
                  <DetailField label="Away team">
                    {teamLink(record.awayTeamId, record.awayTeam?.name)}
                  </DetailField>
                  <DetailField label="Score">
                    {formatScore(record.homeScore, record.awayScore)}
                  </DetailField>
                  <DetailField label="State" value={record.state} />
                  <DetailField label="Round" value={record.round} />
                  <DetailField label="Season" value={record.season} />
                  <DetailField label="Venue">
                    {record.venueId ? (
                      <Link
                        to={`/venues/edit/${record.venueId}`}
                        className="hover:underline"
                      >
                        {record.venue?.name ?? record.venueId}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </DetailField>
                  <DetailField
                    label="Venue source"
                    value={record.venueSource}
                  />
                  <DetailField label="Ticket link">
                    {record.ticketUrl ? (
                      <a
                        href={record.ticketUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="break-all hover:underline"
                      >
                        {record.ticketUrl}
                      </a>
                    ) : (
                      "—"
                    )}
                  </DetailField>
                  <DetailField label="Prices">
                    {record.pricing?.length
                      ? record.pricing
                          .map((p) => `${p.label}: ${p.amount} ${p.currency}`)
                          .join(", ")
                      : "—"}
                  </DetailField>
                  <DetailField label="Tags">
                    {record.tags?.length ? record.tags.join(", ") : "—"}
                  </DetailField>
                  <DetailField label="Status" value={record.status} />
                  <DetailField
                    label="Hidden"
                    value={
                      record.hide == null
                        ? undefined
                        : record.hide
                          ? "Yes"
                          : "No"
                    }
                  />
                </DetailGrid>
              </DetailSection>

              <DetailSection
                title="Source"
                icon={<FingerprintIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="ID" mono>
                    <span className="inline-flex items-center gap-1">
                      {record.id}
                      <CopyButton text={record.id} message="Copied match ID" />
                    </span>
                  </DetailField>
                  <DetailField
                    label="Highlightly ID"
                    value={record.metadata?.sourceId}
                    mono
                  />
                </DetailGrid>
                <ReadOnlyNote />
              </DetailSection>
            </div>

            <MatchDetailsSection
              matchId={record.id}
              sides={{
                homeTeamId: record.homeTeamId,
                awayTeamId: record.awayTeamId,
                homeName: record.homeTeam?.name ?? record.homeTeam?.shortName,
                awayName: record.awayTeam?.name ?? record.awayTeam?.shortName,
              }}
            />

            <SystemSection record={record} />

            <RawJsonCard data={raw} />
          </>
        )}
      </div>
    </div>
  );
};
